import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { PaymentRowType, Scenario, ScheduleType } from '../../shared/types'
import PaymentScheduleRow from './payment-schedule-row'
import PaymentScheduleTable from './payment-schedule-table'
import ScenarioCardHeader from './scenario-card-header'
import ScenarioInputFields from './scenario-input-fields'
import {
  getRowPayoutValue,
  normalizePaymentRowToPercentage,
} from './conversion-utils'
import { validatePaymentSchedule } from './validation-utils'

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
interface ScenarioCardProps {
  scenario: Scenario
  index: number
  scheduleType: ScheduleType
  depositAmount?: number
  onDelete: (id: string) => void
  onChange: (id: string, updatedScenario: Scenario) => void
  validationError?: string
  enablePartialMaturitiesByDollarAmount?: boolean
}

export default function ScenarioCard({
  scenario,
  index,
  scheduleType,
  depositAmount,
  onDelete,
  onChange,
  validationError,
  enablePartialMaturitiesByDollarAmount = false,
}: ScenarioCardProps) {
  const theme = useTheme()

  const totalPercentage =
    Math.round(
      scenario.paymentSchedule.reduce(
        (sum, row) =>
          sum +
          normalizePaymentRowToPercentage(
            row,
            enablePartialMaturitiesByDollarAmount,
            depositAmount ?? 0
          ),
        0
      ) * 1000
    ) / 1000

  const [localError, setLocalError] = useState<string>('')
  const [hoverDelete, setHoverDelete] = useState<boolean>(false)
  const [hoverDeleteRow, setHoverDeleteRow] = useState<number | null>(null)

  // Track which fields have been touched for validation
  const [touched, setTouched] = useState({
    proposedRate: false,
    wamYears: false,
    paymentRows: scenario.paymentSchedule.map(() => ({
      payoutPercentage: false,
      paymentDate: false,
    })),
    scenarioInteracted: false,
  })

  useEffect(() => {
    // Only run validation if all payment fields are filled out
    // Treat 0 as empty since empty fields are initialized to 0
    const allFieldsFilled = scenario.paymentSchedule.every((row) => {
      const amount = getRowPayoutValue(
        row,
        enablePartialMaturitiesByDollarAmount
      )
      return amount > 0 && row.paymentDate != null
    })

    if (allFieldsFilled) {
      setLocalError(
        validatePaymentSchedule(
          scenario.paymentSchedule,
          scheduleType,
          depositAmount,
          enablePartialMaturitiesByDollarAmount
        )
      )
    } else {
      // Clear error if not all fields are filled - red borders will handle this
      setLocalError('')
    }
  }, [
    scenario.paymentSchedule,
    scheduleType,
    depositAmount,
    enablePartialMaturitiesByDollarAmount,
  ])

  const handleProposedRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value =
      e.target.value === '' ? null : Number.parseFloat(e.target.value)
    onChange(scenario.id, {
      ...scenario,
      proposedRate: value,
    })
    setTouched((prev) => ({
      ...prev,
      proposedRate: true,
      scenarioInteracted: true,
    }))
  }

  const handleWamYearsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value =
      e.target.value === '' ? null : Number.parseFloat(e.target.value)
    onChange(scenario.id, { ...scenario, wamYears: value })
    setTouched((prev) => ({
      ...prev,
      wamYears: true,
      scenarioInteracted: true,
    }))
  }

  const handleAddPaymentRow = useCallback(() => {
    const updatedScenario = {
      ...scenario,
      paymentSchedule: [
        ...scenario.paymentSchedule,
        {
          payoutAmount: 0,
          payoutPercentage: 0,
          paymentDate: null,
          inputType: PaymentRowType.PERCENTAGE,
        },
      ],
    }
    onChange(scenario.id, updatedScenario)

    setTouched((prev) => ({
      ...prev,
      paymentRows: [
        ...prev.paymentRows,
        { payoutPercentage: false, paymentDate: false },
      ],
    }))
  }, [scenario, onChange])

  const handleDeletePaymentRow = useCallback(
    (index: number) => {
      const updatedSchedule = [...scenario.paymentSchedule]
      updatedSchedule.splice(index, 1)
      onChange(scenario.id, { ...scenario, paymentSchedule: updatedSchedule })
    },
    [scenario, onChange]
  )

  // Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
  const handlePayoutPercentageChange = useCallback(
    (index: number, rawValue: string) => {
      // Prevent changes to last row in Book Value mode
      const isLastRow = index === scenario.paymentSchedule.length - 1
      if (isLastRow && scheduleType === ScheduleType.BOOK_VALUE) {
        return
      }

      const inputValue = rawValue.trim()
      const currentRow = scenario.paymentSchedule[index]
      const isPercentageMode =
        (currentRow.inputType ?? PaymentRowType.PERCENTAGE) ===
        PaymentRowType.PERCENTAGE

      // Parse and validate the input
      let value: number | string = inputValue
      if (inputValue !== '') {
        const numericValue = Number.parseFloat(inputValue)
        // Check for NaN and clamp value if in percentage mode
        if (!Number.isNaN(numericValue)) {
          value = isPercentageMode
            ? Math.max(0, Math.min(100, numericValue))
            : Math.max(0, numericValue)
        }
      }

      // Determine the final amount value
      let amount = 0
      if (value !== '' && typeof value === 'number') {
        amount = value
      }

      const updatedSchedule = [...scenario.paymentSchedule]
      updatedSchedule[index] = {
        ...updatedSchedule[index],
        payoutAmount: enablePartialMaturitiesByDollarAmount ? amount : 0,
        payoutPercentage: enablePartialMaturitiesByDollarAmount ? 0 : amount,
      }
      onChange(scenario.id, { ...scenario, paymentSchedule: updatedSchedule })

      const updatedPaymentRows = [...touched.paymentRows]
      updatedPaymentRows[index] = {
        ...updatedPaymentRows[index],
        payoutPercentage: true,
      }
      setTouched((prev) => ({
        ...prev,
        paymentRows: updatedPaymentRows,
        scenarioInteracted: true,
      }))
    },
    [
      scenario,
      onChange,
      touched.paymentRows,
      scheduleType,
      enablePartialMaturitiesByDollarAmount,
    ]
  )

  const handlePaymentDateChange = useCallback(
    (index: number, date: Date | string | null) => {
      const updatedSchedule = [...scenario.paymentSchedule]
      updatedSchedule[index] = {
        ...updatedSchedule[index],
        paymentDate: date,
      }
      onChange(scenario.id, { ...scenario, paymentSchedule: updatedSchedule })

      const updatedPaymentRows = [...touched.paymentRows]
      updatedPaymentRows[index] = {
        ...updatedPaymentRows[index],
        paymentDate: true,
      }
      setTouched((prev) => ({
        ...prev,
        paymentRows: updatedPaymentRows,
        scenarioInteracted: true,
      }))
    },
    [scenario, onChange, touched.paymentRows]
  )

  const handleTogglePaymentType = useCallback(
    (index: number) => {
      // Prevent changes to last row in Book Value mode
      const isLastRow = index === scenario.paymentSchedule.length - 1
      if (isLastRow && scheduleType === ScheduleType.BOOK_VALUE) {
        return
      }

      const updatedSchedule = [...scenario.paymentSchedule]
      const currentRowType =
        updatedSchedule[index].inputType ?? PaymentRowType.PERCENTAGE
      const newRowType =
        currentRowType === PaymentRowType.PERCENTAGE
          ? PaymentRowType.DOLLAR
          : PaymentRowType.PERCENTAGE

      updatedSchedule[index] = {
        ...updatedSchedule[index],
        inputType: newRowType,
      }
      onChange(scenario.id, { ...scenario, paymentSchedule: updatedSchedule })
    },
    [scenario, onChange, scheduleType]
  )

  const handleSortByDate = (newSortOrder: 'asc' | 'desc') => {
    const sortedSchedule = [...scenario.paymentSchedule].sort((a, b) => {
      if (a.paymentDate == null && b.paymentDate == null) return 0
      if (a.paymentDate == null) return 1 // Null dates go at the end
      if (b.paymentDate == null) return -1

      const dateA = new Date(a.paymentDate).getTime()
      const dateB = new Date(b.paymentDate).getTime()

      return newSortOrder === 'asc' ? dateA - dateB : dateB - dateA
    })

    onChange(scenario.id, { ...scenario, paymentSchedule: sortedSchedule })
  }

  // Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
  // Memoize rendered payment schedule components to avoid unnecessary re-renders
  const memoizedPaymentSchedule = useMemo(
    () =>
      scenario.paymentSchedule.map((row, rowIndex) => {
        return (
          <PaymentScheduleRow
            // eslint-disable-next-line react-x/no-array-index-key
            key={`payment-${scenario.id}-${rowIndex}`}
            row={row}
            rowIndex={rowIndex}
            scheduleType={scheduleType}
            totalRows={scenario.paymentSchedule.length}
            touched={
              touched.paymentRows[rowIndex] ?? {
                payoutPercentage: false,
                paymentDate: false,
              }
            }
            hoverDeleteRow={hoverDeleteRow}
            canDelete={scenario.paymentSchedule.length > 1}
            onPayoutPercentageChange={handlePayoutPercentageChange}
            onPaymentDateChange={handlePaymentDateChange}
            onTogglePaymentType={handleTogglePaymentType}
            onDeleteRow={handleDeletePaymentRow}
            onMouseEnterDelete={setHoverDeleteRow}
            onMouseLeaveDelete={() => setHoverDeleteRow(null)}
          />
        )
      }),
    [
      scenario.paymentSchedule,
      scenario.id,
      scheduleType,
      touched.paymentRows,
      hoverDeleteRow,
      handlePayoutPercentageChange,
      handlePaymentDateChange,
      handleTogglePaymentType,
      handleDeletePaymentRow,
    ]
  )

  return (
    <Card
      sx={{
        position: 'relative',
        backgroundColor: hoverDelete
          ? 'rgba(211, 47, 47, 0.04)'
          : 'background.paper',
        width: '100%',
        height: '100%',
        border: '1px solid',
        borderColor: hoverDelete ? 'error.light' : theme.palette.grey[200],
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <ScenarioCardHeader
          index={index}
          onDelete={() => {
            setHoverDelete(true)
            onDelete(scenario.id)
          }}
        />

        <ScenarioInputFields
          proposedRate={scenario.proposedRate}
          wamYears={scenario.wamYears}
          onProposedRateChange={handleProposedRateChange}
          onWamYearsChange={handleWamYearsChange}
        />

        <PaymentScheduleTable
          scheduleType={scheduleType}
          totalPercentage={totalPercentage}
          onAddRow={handleAddPaymentRow}
          onSort={handleSortByDate}
        >
          {memoizedPaymentSchedule}
        </PaymentScheduleTable>

        {((validationError != null && validationError !== '') ||
          localError !== '') && (
          <Box
            sx={{
              mt: 1,
              bgcolor: 'rgba(211, 47, 47, 0.08)',
              color: 'error.main',
              p: 1,
              borderRadius: 1,
              borderLeft: '4px solid',
              borderColor: 'error.main',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ErrorOutlineIcon color="error" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2" color="error">
              {validationError != null && validationError !== ''
                ? validationError
                : localError}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
